import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import type {
  AddTeamAssistantAllowlistRequest,
  AddTeamAssistantAllowlistResponse,
  ListTeamAssistantWorkspacePeopleResponse,
  ListTeamAssistantAllowlistResponse,
  ListTeamAssistantsResponse,
  RemoveTeamAssistantAllowlistResponse,
  TeamAssistant,
  TeamAssistantResponse,
  TeamAssistantAccessMode,
  TeamAssistantAllowlistEntry,
  TeamAssistantWorkspacePerson,
  UpdateTeamAssistantRequest,
} from '@dreamai/types';
import { apiErrorMessage } from './organisationLabels';
import { TeamAssistantChannels } from './TeamAssistantChannels';
import { useInstallReturn } from './teamAssistantInstallation';
import './delete-account-modal.scss';
import './organisation-team-assistants.scss';

interface OrganisationTeamAssistantsProps {
  organisationId: string;
  /** Whether the Team Assistants tab is the one showing */
  visible: boolean;
  /**
   * Reports whether the tab may be shown at all: `true` once the list call
   * has succeeded, `false` while loading or when the backend says the
   * feature is off (404) or the caller may not administer it (403).
   */
  onAvailabilityChange: (available: boolean) => void;
  canAddTeamAssistant: boolean | null;
  onSubscriptionRequired: () => void;
}

type Feedback = { type: 'success' | 'error'; message: string } | null;

export const STATUS_LABELS: Record<TeamAssistant['status'], string> = {
  active: 'Active',
  paused: 'Paused',
  archived: 'Archived',
};

const ACCESS_LABELS: Record<TeamAssistantAccessMode, string> = {
  allowlist: 'Only specific people',
  everyone_in_workspace: 'Anyone in the connected workspace',
};

function teamAssistantTrialDate(date: string | null): string {
  return date
    ? new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
      })
    : 'the end of your free trial';
}

function teamAssistantSeatSummary(assistant: TeamAssistant): string {
  if (!assistant.seats) return '';
  const { billingStartedAt, people, seats, trialEndsAt } = assistant.seats;
  const peopleLabel =
    assistant.accessMode === 'everyone_in_workspace'
      ? 'Slack members'
      : 'selected people';
  const seatLabel = (count: number) =>
    `${count} subscription ${count === 1 ? 'seat' : 'seats'}`;
  const fallback = `${assistant.name} uses 1 subscription seat per 40 Slack members.`;

  if (billingStartedAt) {
    if (seats === null || people === null) return fallback;
    return `${assistant.name} uses ${seatLabel(seats)} (${people} ${peopleLabel}).`;
  }

  const freeUntil = teamAssistantTrialDate(trialEndsAt);
  if (seats === null || people === null) {
    return `Free until ${freeUntil}. After that ${fallback}`;
  }
  return `Free until ${freeUntil}. After that ${assistant.name} uses ${seatLabel(seats)} (${people} ${peopleLabel}).`;
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

interface AccessModeFieldProps {
  assistantName: string;
  value: TeamAssistantAccessMode;
  busy: boolean;
  onChange: (mode: TeamAssistantAccessMode) => void;
}

const AccessModeField: FC<AccessModeFieldProps> = ({
  assistantName,
  value,
  busy,
  onChange,
}) => (
  <fieldset className='team-assistant-access'>
    <legend className='field-label'>Who can talk to {assistantName}</legend>
    {(['everyone_in_workspace', 'allowlist'] as TeamAssistantAccessMode[]).map(
      (mode) => (
        <label key={mode} className='organisation-checkbox'>
          <input
            type='radio'
            name='team-assistant-access-mode'
            value={mode}
            checked={value === mode}
            disabled={busy}
            onChange={() => onChange(mode)}
          />
          <span>{ACCESS_LABELS[mode]}</span>
        </label>
      ),
    )}
  </fieldset>
);

interface RemoveModalProps {
  assistant: TeamAssistant;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

const RemoveTeamAssistantModal: FC<RemoveModalProps> = ({
  assistant,
  busy,
  error,
  onClose,
  onConfirm,
}) => {
  const handleClose = () => {
    if (!busy) onClose();
  };
  return (
    <div
      className='delete-account-modal-overlay'
      onClick={handleClose}
      data-testid='remove-team-assistant-modal'
    >
      <div
        className='delete-account-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='remove-team-assistant-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='remove-team-assistant-title'>
            Remove {assistant.name}
          </p>
          <button
            type='button'
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={busy}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>
        <div className='modal-body'>
          <h3 className='body-title'>This cannot be undone</h3>
          <div className='body-text'>
            <p>
              {assistant.installation ? (
                <>
                  {assistant.name} will be uninstalled from{' '}
                  {assistant.installation.externalWorkspaceName ??
                    assistant.installation.externalWorkspaceId}{' '}
                  in Slack and lose all its memory and conversations. Your
                  Handbook isn't affected.
                </>
              ) : (
                <>
                  {assistant.name} will lose all its memory and conversations.
                  Your Handbook isn't affected.
                </>
              )}{' '}
            </p>
            <p>You can add Reve to Slack again afterwards.</p>
          </div>
        </div>
        <div className='modal-footer'>
          {error && <p className='delete-error'>{error}</p>}
          <button
            type='button'
            className='btn-delete-dismiss'
            onClick={handleClose}
            disabled={busy}
          >
            Keep Reve
          </button>
          <button
            type='button'
            className='btn-delete-confirm'
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Removing…' : 'Remove Reve'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Settings → Organisation → Team Assistants. An organisation has at most one
 * Team Assistant that is not archived. Mounted for admins on every tab so the
 * list call (which doubles as the availability check) runs as soon as the
 * organisation loads; renders nothing until `visible`.
 */
const OrganisationTeamAssistants: FC<OrganisationTeamAssistantsProps> = ({
  organisationId,
  visible,
  onAvailabilityChange,
  canAddTeamAssistant,
  onSubscriptionRequired,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisationId}/team-assistants`;

  const [assistants, setAssistants] = useState<TeamAssistant[] | null>(null);
  const [allowlist, setAllowlist] = useState<
    TeamAssistantAllowlistEntry[] | null
  >(null);
  const [allowlistError, setAllowlistError] = useState<string | null>(null);
  const [workspacePeople, setWorkspacePeople] = useState<
    TeamAssistantWorkspacePerson[] | null
  >(null);
  const [workspacePeopleError, setWorkspacePeopleError] = useState<
    string | null
  >(null);
  const [workspacePeopleRefresh, setWorkspacePeopleRefresh] = useState(0);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const installReturn = useInstallReturn();

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const fetchAssistants = useCallback(async () => {
    const { data } = await api.get<ListTeamAssistantsResponse>(
      base,
      await authHeaders(),
    );
    return data.teamAssistants;
  }, [base, authHeaders]);

  useEffect(() => {
    if (installReturn) setFeedback(installReturn);
  }, [installReturn]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const teamAssistants = await fetchAssistants();
        if (cancelled) return;
        setAssistants(teamAssistants);
        onAvailabilityChange(true);
      } catch (err) {
        if (cancelled) return;
        const status = httpStatus(err);
        if (status !== 404 && status !== 403) {
          console.error('Error loading team assistants:', err);
        }
        onAvailabilityChange(false);
      }
    })();
    return () => {
      cancelled = true;
      onAvailabilityChange(false);
    };
  }, [fetchAssistants, onAvailabilityChange]);

  const current = assistants?.find((a) => a.status !== 'archived') ?? null;
  const currentId = current?.id ?? null;
  const canLoadWorkspacePeople = Boolean(
    currentId && current?.accessMode === 'allowlist' && current.installation,
  );

  useEffect(() => {
    if (!currentId) {
      setAllowlist(null);
      setAllowlistError(null);
      return;
    }
    let cancelled = false;
    setAllowlist(null);
    setAllowlistError(null);
    (async () => {
      try {
        const { data } = await api.get<ListTeamAssistantAllowlistResponse>(
          `${base}/${currentId}/allowlist`,
          await authHeaders(),
        );
        if (!cancelled) setAllowlist(data.entries);
      } catch (err) {
        console.error('Error loading team assistant allowed emails:', err);
        if (!cancelled) {
          setAllowlistError(
            apiErrorMessage(err, 'Could not load the allowed emails.'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, currentId, authHeaders]);

  useEffect(() => {
    if (!canLoadWorkspacePeople || !currentId) {
      setWorkspacePeople(null);
      setWorkspacePeopleError(null);
      setPeoplePickerOpen(false);
      setPeopleQuery('');
      return;
    }
    let cancelled = false;
    setWorkspacePeople(null);
    setWorkspacePeopleError(null);
    (async () => {
      try {
        const { data } =
          await api.get<ListTeamAssistantWorkspacePeopleResponse>(
            `${base}/${currentId}/workspace-people`,
            await authHeaders(),
          );
        if (!cancelled) setWorkspacePeople(data.people);
      } catch (err) {
        console.error('Error loading Slack workspace members:', err);
        if (!cancelled) {
          setWorkspacePeopleError('Could not load your Slack members.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    authHeaders,
    base,
    canLoadWorkspacePeople,
    currentId,
    workspacePeopleRefresh,
  ]);

  const availablePeople = useMemo(() => {
    if (!workspacePeople) return [];
    const needle = peopleQuery.trim().toLowerCase();
    const allowlistedEmails = new Set(
      (allowlist ?? []).flatMap((entry) =>
        entry.email ? [entry.email.toLowerCase()] : [],
      ),
    );
    return workspacePeople.filter((person) => {
      const selected =
        allowlist === null
          ? person.selected
          : allowlistedEmails.has(person.email.toLowerCase());
      const matchesQuery =
        !needle ||
        person.name.toLowerCase().includes(needle) ||
        person.email.toLowerCase().includes(needle);
      return !selected && matchesQuery;
    });
  }, [allowlist, peopleQuery, workspacePeople]);

  const run = useCallback(
    async (
      key: string,
      action: () => Promise<string | void>,
      fallback: string,
    ) => {
      setFeedback(null);
      setBusyKey(key);
      try {
        const message = await action();
        if (message) setFeedback({ type: 'success', message });
        return true;
      } catch (err) {
        console.error(fallback, err);
        setFeedback({ type: 'error', message: apiErrorMessage(err, fallback) });
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const replaceAssistant = (next: TeamAssistant) =>
    setAssistants((prev) =>
      prev
        ? prev.some((a) => a.id === next.id)
          ? prev.map((a) => (a.id === next.id ? next : a))
          : [...prev, next]
        : [next],
    );

  const patch = (
    assistant: TeamAssistant,
    body: UpdateTeamAssistantRequest,
    key: string,
    message: string,
    fallback: string,
  ) =>
    run(
      key,
      async () => {
        const { data } = await api.patch<TeamAssistantResponse>(
          `${base}/${assistant.id}`,
          body,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        replaceAssistant(data.teamAssistant);
        return message;
      },
      fallback,
    );

  const handleRemove = async () => {
    if (!current) return;
    setRemoveError(null);
    setBusyKey('remove');
    try {
      const { data } = await api.post<TeamAssistantResponse>(
        `${base}/${current.id}/archive`,
        {},
        await authHeaders(),
      );
      if (!data.success) throw new Error();
      setAssistants(await fetchAssistants());
      setRemoving(false);
      setFeedback({ type: 'success', message: `${current.name} removed.` });
    } catch (err) {
      console.error('Could not remove the Team Assistant.', err);
      setRemoveError(
        apiErrorMessage(err, 'Could not remove the Team Secretary.'),
      );
    } finally {
      setBusyKey(null);
    }
  };

  const handleAddToSlack = () => {
    if (canAddTeamAssistant === false) {
      onSubscriptionRequired();
      return;
    }
    run(
      'connect',
      async () => {
        try {
          let assistant = current;
          if (!assistant) {
            const { data } = await api.post<TeamAssistantResponse>(
              base,
              {},
              await authHeaders(),
            );
            if (!data.success) throw new Error();
            assistant = data.teamAssistant;
            replaceAssistant(assistant);
          }

          const { headers } = await authHeaders();
          const { data } = await api.get<{ url: string }>(
            '/slack-ta-install/start',
            {
              params: { organisationId, agentId: assistant.id },
              headers: { ...headers, Accept: 'application/json' },
            },
          );
          if (!data.url) throw new Error();
          window.location.assign(data.url);
        } catch (err) {
          const response = (
            err as {
              response?: {
                status?: number;
                data?: { code?: string };
              };
            }
          )?.response;
          if (
            response?.status === 402 &&
            response.data?.code === 'subscription_required'
          ) {
            onSubscriptionRequired();
            return;
          }
          throw err;
        }
      },
      'Could not start the Slack installation.',
    );
  };

  const handleAddPerson = (person: TeamAssistantWorkspacePerson) => {
    if (!current) return;
    run(
      `allowlist:add:${person.externalUserId}`,
      async () => {
        const body: AddTeamAssistantAllowlistRequest = {
          externalUserIds: [person.externalUserId],
        };
        const { data } = await api.post<AddTeamAssistantAllowlistResponse>(
          `${base}/${current.id}/allowlist`,
          body,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        setAllowlist((prev) => [...(prev ?? []), ...data.added]);
        return `${person.name} added.`;
      },
      'Could not add that person.',
    );
  };

  const handleRemoveEmail = (entry: TeamAssistantAllowlistEntry) => {
    if (!current) return;
    run(
      `allowlist:${entry.id}`,
      async () => {
        const { data } = await api.delete<RemoveTeamAssistantAllowlistResponse>(
          `${base}/${current.id}/allowlist/${entry.id}`,
          await authHeaders(),
        );
        if (!data.success) throw new Error();
        setAllowlist((prev) =>
          prev ? prev.filter((x) => x.id !== entry.id) : prev,
        );
        return `${entry.email ?? 'Hidden address'} removed.`;
      },
      'Could not remove that address.',
    );
  };

  if (!visible || !assistants) return null;

  const busy = busyKey !== null;

  const renderFeedback = () =>
    feedback && (
      <p
        className={`organisation-feedback ${feedback.type}`}
        role={feedback.type === 'error' ? 'alert' : 'status'}
      >
        {feedback.message}
      </p>
    );

  if (!current || (!current.installation && current.status === 'active')) {
    return (
      <div
        className='organisation-card'
        data-testid='organisation-team-assistants'
      >
        <h3 className='organisation-card-title'>Team Secretaries</h3>
        <p className='organisation-card-text'>
          Reve is a shared secretary your team can talk to in Slack. It belongs
          to the organisation rather than to any one member, and you decide who
          may talk to it.
        </p>

        {!current && (
          <p className='organisation-empty'>No Team Secretary yet.</p>
        )}
        <div className='organisation-actions'>
          <button
            type='button'
            className='btn-primary'
            disabled={busy}
            onClick={handleAddToSlack}
          >
            {busyKey === 'connect' ? 'Opening Slack…' : 'Add Reve to Slack'}
          </button>
        </div>

        {(!current || current.status === 'active') && (
          <p
            className='organisation-card-text'
            data-testid='team-assistant-empty-seat-copy'
          >
            Free for 7 days, then 1 subscription seat per 40 Slack members.
          </p>
        )}

        {renderFeedback()}
      </div>
    );
  }

  return (
    <div
      className='organisation-card'
      data-testid='organisation-team-assistants'
    >
      <div className='team-assistant-header'>
        <div>
          <div className='team-assistant-title'>
            <h3 className='organisation-card-title'>{current.name}</h3>
            <span
              className={`team-assistant-status status-${current.status}`}
              data-testid='team-assistant-status'
            >
              {STATUS_LABELS[current.status]}
            </span>
          </div>
          {current.installation && (
            <p className='team-assistant-meta'>
              Connected to{' '}
              {current.installation.externalWorkspaceName ??
                current.installation.externalWorkspaceId}
            </p>
          )}
          {current.installation && (
            <p
              className='team-assistant-meta'
              data-testid='team-assistant-seat-summary'
            >
              {teamAssistantSeatSummary(current)}
            </p>
          )}
        </div>
        <div className='organisation-actions'>
          <button
            type='button'
            className='btn-secondary danger'
            disabled={busy}
            onClick={() => {
              setRemoveError(null);
              setRemoving(true);
            }}
          >
            Remove Reve
          </button>
        </div>
      </div>

      {!current.installation && (
        <div
          className='organisation-subcard'
          data-testid='team-assistant-installation'
        >
          <h4 className='organisation-subcard-title'>Slack</h4>
          <p className='organisation-card-text'>
            Resume the secretary before adding it to Slack.
          </p>
          <div className='organisation-actions'>
            <button type='button' className='btn-primary' disabled>
              Add to Slack
            </button>
          </div>
        </div>
      )}

      {current.installation && (
        <TeamAssistantChannels
          base={base}
          assistant={current}
          disabled={busy}
          authHeaders={authHeaders}
          onFeedback={setFeedback}
        />
      )}

      <div className='organisation-subcard'>
        <h4 className='organisation-subcard-title'>Access</h4>
        <AccessModeField
          assistantName={current.name}
          value={current.accessMode}
          busy={busy}
          onChange={(accessMode) =>
            patch(
              current,
              { accessMode },
              'access',
              accessMode === 'allowlist'
                ? `Only the people you pick can talk to ${current.name} now.`
                : `Anyone in the connected workspace can talk to ${current.name} now.`,
              `Could not change who can talk to ${current.name}.`,
            )
          }
        />
      </div>

      {current.accessMode === 'allowlist' && (
        <div
          className='organisation-subcard'
          data-testid='team-assistant-allowlist'
        >
          <h4 className='organisation-subcard-title'>Specific people</h4>
          {allowlistError && (
            <p className='organisation-feedback error' role='alert'>
              {allowlistError}
            </p>
          )}
          {allowlist === null && !allowlistError && (
            <p className='organisation-empty'>Loading people…</p>
          )}
          {allowlist && allowlist.length === 0 && (
            <p className='organisation-empty'>No one picked yet.</p>
          )}
          {allowlist && allowlist.length > 0 && (
            <ul className='team-assistant-allowlist'>
              {allowlist.map((entry) => {
                const removingEntry = busyKey === `allowlist:${entry.id}`;
                const person = entry.email
                  ? workspacePeople?.find(
                      (candidate) =>
                        candidate.email.toLowerCase() ===
                        entry.email?.toLowerCase(),
                    )
                  : undefined;
                return (
                  <li
                    key={entry.id}
                    className='team-assistant-allowlist-item'
                    data-testid='team-assistant-allowlist-entry'
                  >
                    <div className='team-assistant-allowlist-info'>
                      <span
                        className={`team-assistant-name${entry.email ? '' : ' hidden-address'}`}
                      >
                        {person?.name ?? entry.email ?? 'Hidden address'}
                      </span>
                      {person && (
                        <span className='team-assistant-meta'>
                          {person.email}
                        </span>
                      )}
                    </div>
                    <button
                      type='button'
                      className='btn-secondary danger'
                      disabled={busy}
                      aria-label={`Remove ${entry.email ?? 'hidden address'}`}
                      onClick={() => handleRemoveEmail(entry)}
                    >
                      {removingEntry ? '…' : 'Remove'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {workspacePeopleError ? (
            <p className='organisation-feedback error' role='alert'>
              {workspacePeopleError}
            </p>
          ) : peoplePickerOpen && workspacePeople === null ? (
            <div
              className='team-assistant-people-picker'
              data-testid='team-assistant-people-picker'
            >
              <p className='organisation-empty'>Loading Slack members…</p>
            </div>
          ) : workspacePeople === null ? (
            <p className='organisation-empty'>Loading Slack members…</p>
          ) : peoplePickerOpen ? (
            <div
              className='team-assistant-people-picker'
              data-testid='team-assistant-people-picker'
            >
              <div className='organisation-field-row'>
                <input
                  type='search'
                  className='field-input'
                  aria-label='Search people'
                  placeholder='Search people'
                  value={peopleQuery}
                  autoFocus
                  disabled={busy}
                  onChange={(e) => setPeopleQuery(e.target.value)}
                />
                <button
                  type='button'
                  className='btn-secondary'
                  disabled={busy}
                  onClick={() => {
                    setPeoplePickerOpen(false);
                    setPeopleQuery('');
                  }}
                >
                  Cancel
                </button>
              </div>
              {availablePeople.length === 0 && (
                <p className='organisation-empty'>
                  {peopleQuery.trim()
                    ? 'No one matches that search.'
                    : workspacePeople.length === 0
                      ? 'No Slack members are available.'
                      : 'Everyone in the workspace is already picked.'}
                </p>
              )}
              {availablePeople.length > 0 && (
                <ul className='team-assistant-people-options' role='listbox'>
                  {availablePeople.map((person) => (
                    <li key={person.externalUserId} role='option'>
                      <button
                        type='button'
                        className='team-assistant-people-option'
                        disabled={busy}
                        aria-label={`Add ${person.name}`}
                        data-testid='team-assistant-people-option'
                        onClick={() => handleAddPerson(person)}
                      >
                        <span className='team-assistant-people-name'>
                          {person.name}
                        </span>
                        <span className='team-assistant-meta'>
                          {person.email}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <button
              type='button'
              className='btn-secondary'
              disabled={busy}
              onClick={() => {
                setPeoplePickerOpen(true);
                setPeopleQuery('');
                setWorkspacePeople(null);
                setWorkspacePeopleError(null);
                setWorkspacePeopleRefresh((refresh) => refresh + 1);
              }}
            >
              Add people
            </button>
          )}
        </div>
      )}

      {renderFeedback()}

      {removing && (
        <RemoveTeamAssistantModal
          assistant={current}
          busy={busyKey === 'remove'}
          error={removeError}
          onClose={() => setRemoving(false)}
          onConfirm={handleRemove}
        />
      )}
    </div>
  );
};

export default OrganisationTeamAssistants;
